import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { compactSelectClass } from '../styles';
import { fetchOrgLedger } from './api';
import type { OrdersMeta, OrgLedgerRow } from './types';

interface Props {
  meta: OrdersMeta;
}

const OrgLedgerPanel: React.FC<Props> = ({ meta }) => {
  const [rows, setRows] = useState<OrgLedgerRow[]>([]);
  const [orgFilter, setOrgFilter] = useState<number | ''>('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchOrgLedger({
          org: orgFilter || undefined,
          type: typeFilter || undefined,
          status: statusFilter || undefined,
        });
        if (!cancelled) setRows(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load ledger');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [orgFilter, typeFilter, statusFilter]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <select
          className={compactSelectClass}
          value={orgFilter}
          onChange={(e) => setOrgFilter(e.target.value ? Number(e.target.value) : '')}
          aria-label="Filter by organization"
        >
          <option value="">All orgs</option>
          {meta.orgs.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
        <select
          className={compactSelectClass}
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          aria-label="Filter by type"
        >
          <option value="">All types</option>
          {meta.ledger_types.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <select
          className={compactSelectClass}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          {meta.ledger_statuses.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </div>
      {error && <div className="rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#8A8A8A] py-8">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="overflow-x-auto border border-[#E8E8E8] rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-[#FAFAFA] text-[12px] text-[#626262]">
              <tr>
                <th className="text-left px-3 py-2 font-medium">ID</th>
                <th className="text-left px-3 py-2 font-medium">Organization</th>
                <th className="text-right px-3 py-2 font-medium">Amount</th>
                <th className="text-left px-3 py-2 font-medium">Type</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-left px-3 py-2 font-medium">Reason</th>
                <th className="text-left px-3 py-2 font-medium">Order</th>
                <th className="text-left px-3 py-2 font-medium">User</th>
                <th className="text-left px-3 py-2 font-medium">Created</th>
                <th className="text-left px-3 py-2 font-medium">Allocations</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-[#F0F0F0] align-top">
                  <td className="px-3 py-2">{row.id}</td>
                  <td className="px-3 py-2">{row.org_name || '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{row.amount}</td>
                  <td className="px-3 py-2">{row.type}</td>
                  <td className="px-3 py-2">{row.status}</td>
                  <td className="px-3 py-2 max-w-[240px] truncate" title={row.reason}>
                    {row.reason || '—'}
                  </td>
                  <td className="px-3 py-2">{row.order ?? '—'}</td>
                  <td className="px-3 py-2">{row.user_email || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-[#626262]">
                    {row.allocations.length === 0
                      ? '—'
                      : row.allocations
                          .map((a) => `batch #${a.batch_id}: ${a.amount}`)
                          .join('; ')}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-3 py-8 text-center text-[#8A8A8A]">
                    No ledger entries
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OrgLedgerPanel;
